import { h } from 'vue';
const STATIC_PROPS = {
  value: { required: true },
  tag: { type: String, default: 'div' },
  highlight: Boolean,
};
const VALIDATORS = Object.create(null);

export default {
  inheritAttrs: false,
  emits: ['validate'],
  inject: {
    validationGroup: { default: null },
  },
  props: {
    ...STATIC_PROPS,
  },
  data() {
    return {
      valid: true,
      touched: false,
      failedRule: null,
      isChanged: false,
      attrsSnapshot: null,
      // Snapshot which validator props were actually passed. Stored once in
      // data so the rulesProps watcher only fires when a rule's value
      // changes, not when any unrelated prop on the component changes.
      rules: Object.keys(this.$props).filter(
        (key) => !STATIC_PROPS[key] && this[key] !== undefined,
      ),
    };
  },
  watch: {
    rulesProps: 'updateValidityLater',
  },
  computed: {
    rulesProps() {
      return this.rules.map((rule) => this[rule]);
    },
    rootAttrs() {
      const attrs = {
        onFocusin: this.trackValueAndValidity,
        onFocusout: this.validateIfChanged,
        class: [this.$attrs.class, this.highlight ? this.validityClass : null],
        style: this.$attrs.style,
      };

      // if it is validation of person identity code
      if (this.rules.indexOf('personId') !== -1) {
        // for situation when user filled field of personId made few backspace and filled it again
        let wasRewritten = false;
        attrs.onInput = (e) => {
          if (e.target.value.length === 11) {
            this.isChanged = true;
            wasRewritten = true;
            this.validateIfChanged();
          } else if (wasRewritten) {
            this.trackValueAndValidity();
            wasRewritten = false;
          }
        };
      }

      return attrs;
    },
    inputAttrs() {
      const { class: _c, style: _s, ...rest } = this.$attrs;
      return rest;
    },
    validityClass() {
      if (!this.touched) {
        return '';
      }

      return this.valid ? 'is-valid' : 'has-error';
    },
    slotProps() {
      return {
        validityClass: this.validityClass,
        valid: this.valid,
        touched: this.touched,
        failedRule: this.failedRule,
        validationPath: this.validationPath,
      };
    },
    valueDomField() {
      return this.$attrs.type === 'checkbox' ? 'checked' : 'value';
    },
    validationPath() {
      if (!this.failedRule) {
        return null;
      }

      let path = '';

      if (this.validationGroup && this.validationGroup.name) {
        path += `${this.validationGroup.name}.`;
      }

      return `${path}validation.${this.failedRule}`;
    },
  },
  methods: {
    setValidity() {
      this.valid = false;
    },
    invalidate(event) {
      event.preventDefault();
      this.updateValidity();
      this.markAsTouched();
    },
    updateValidity() {
      this.updateOnlyValidity();
      this.$emit('validate', this.valid);
    },
    updateOnlyValidity() {
      if (!this.$refs.input) {
        return;
      }
      const { validity } = this.$refs.input;

      this.valid = validity.valid;

      if (validity.customError || validity.valid) {
        this.valid = this.validate();
      }

      this.failedRule = this.valid ? null : this.findFailedRule();
    },
    findFailedRule() {
      const { input } = this.$refs;
      if (!input) {
        return null;
      }
      // eslint-disable-next-line
      for (const prop in input.validity) {
        if (prop !== 'valid' && prop !== 'customError' && input.validity[prop] === true) {
          return prop === 'typeMismatch' ? `${this.$attrs.type}Invalid` : prop;
        }
      }

      return input.validity.customError ? input.validationMessage : null;
    },
    updateValidityLater() {
      return this.$nextTick(this.updateValidity);
    },
    captureAttrsSnapshot() {
      this.attrsSnapshot = JSON.stringify(this.$attrs);
    },
    updateValidityIfAttrsChanged() {
      const nextAttrsSnapshot = JSON.stringify(this.$attrs);

      if (nextAttrsSnapshot === this.attrsSnapshot) {
        return;
      }

      this.attrsSnapshot = nextAttrsSnapshot;
      this.updateValidity();

      if (this.$attrs.disabled) {
        this.resetImmediately();
      }
    },
    validate() {
      if (!this.$refs.input) {
        return true;
      }
      const failedRule = this.rules.find((rule) => {
        const validator = VALIDATORS[rule];

        return validator && !validator(this.value, this[rule], this);
      });

      this.$refs.input.setCustomValidity(failedRule || '');

      return !failedRule;
    },
    reset() {
      const waitForValidityToBeUpdated = this.$nextTick;
      const waitForValueToBeReset = this.$nextTick;

      return waitForValueToBeReset(() => waitForValidityToBeUpdated(this.resetImmediately));
    },
    resetImmediately() {
      this.touched = false;
    },
    markAsTouched() {
      this.touched = true;
    },
    trackValueAndValidity() {
      const wasValid = this.valid;

      this.unwatchValue = this.$watch('value', () => {
        this.isChanged = true;

        if (this.valid) {
          this.stopValueTracking();
        } else if (!wasValid) {
          this.updateValidityLater();
        }
      });
    },
    stopValueTracking() {
      if (this.unwatchValue) {
        this.unwatchValue();
        this.unwatchValue = null;
      }
    },
    validateIfChanged() {
      this.stopValueTracking();

      if (!this.isChanged) {
        return;
      }

      this.isChanged = false;
      this.updateValidityLater();
      this.markAsTouched();
    },
  },
  created() {
    if (this.validationGroup) {
      this.validationGroup.add(this);
    }
  },
  mounted() {
    this.captureAttrsSnapshot();
  },
  updated() {
    this.updateValidityIfAttrsChanged();
  },
  beforeUnmount() {
    if (this.validationGroup) {
      this.validationGroup.remove(this);
    }
  },
  render() {
    let children = this.$slots.default ? this.$slots.default(this.slotProps) : [];

    if (!Array.isArray(children)) {
      children = [children];
    }

    if (this.touched) {
      const slotName = this.valid ? 'valid' : 'error';
      const scopedSlot = this.$slots[slotName] ? this.$slots[slotName](this.slotProps) : null;
      const slot = scopedSlot || this.$slots[slotName];
      children = slot ? children.concat(slot) : children;
    }

    return h(this.tag, this.rootAttrs, [
      h('input', {
        ...this.inputAttrs,
        style: { display: 'none' },
        ref: 'input',
        [this.valueDomField]: this.value,
        onInvalid: this.invalidate,
      }),
      ...children,
    ]);
  },
  registerValidator(name, defs, fn) {
    this.props[name] = defs || {};
    VALIDATORS[name] = fn;
  },
  unregisterValidator(name) {
    if (VALIDATORS[name]) {
      delete VALIDATORS[name];
      delete this.props[name];
    }
  },
};
